/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSkillDistributionsRequest,
  ListSkillDistributionsSecurity,
} from "../models/operations/listskilldistributions.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillDistributionsInfiniteQuery,
  buildSkillDistributionsQuery,
  prefetchSkillDistributions,
  prefetchSkillDistributionsInfinite,
  queryKeySkillDistributions,
  queryKeySkillDistributionsInfinite,
  SkillDistributionsInfiniteQueryData,
  SkillDistributionsPageParams,
  SkillDistributionsQueryData,
} from "./skillDistributions.core.js";
export {
  buildSkillDistributionsInfiniteQuery,
  buildSkillDistributionsQuery,
  prefetchSkillDistributions,
  prefetchSkillDistributionsInfinite,
  queryKeySkillDistributions,
  queryKeySkillDistributionsInfinite,
  type SkillDistributionsInfiniteQueryData,
  type SkillDistributionsPageParams,
  type SkillDistributionsQueryData,
};

export type SkillDistributionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDistributions skills
 *
 * @remarks
 * List active plugin skill distributions for the current project.
 */
export function useSkillDistributions(
  request?: ListSkillDistributionsRequest | undefined,
  security?: ListSkillDistributionsSecurity | undefined,
  options?: QueryHookOptions<
    SkillDistributionsQueryData,
    SkillDistributionsQueryError
  >,
): UseQueryResult<SkillDistributionsQueryData, SkillDistributionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillDistributionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDistributions skills
 *
 * @remarks
 * List active plugin skill distributions for the current project.
 */
export function useSkillDistributionsSuspense(
  request?: ListSkillDistributionsRequest | undefined,
  security?: ListSkillDistributionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SkillDistributionsQueryData,
    SkillDistributionsQueryError
  >,
): UseSuspenseQueryResult<
  SkillDistributionsQueryData,
  SkillDistributionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillDistributionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDistributions skills
 *
 * @remarks
 * List active plugin skill distributions for the current project.
 */
export function useSkillDistributionsInfinite(
  request?: ListSkillDistributionsRequest | undefined,
  security?: ListSkillDistributionsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SkillDistributionsInfiniteQueryData,
    SkillDistributionsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    SkillDistributionsInfiniteQueryData,
    SkillDistributionsPageParams
  >,
  SkillDistributionsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SkillDistributionsInfiniteQueryData,
    SkillDistributionsQueryError,
    InfiniteData<
      SkillDistributionsInfiniteQueryData,
      SkillDistributionsPageParams
    >,
    QueryKey,
    SkillDistributionsPageParams
  >({
    ...buildSkillDistributionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listDistributions skills
 *
 * @remarks
 * List active plugin skill distributions for the current project.
 */
export function useSkillDistributionsInfiniteSuspense(
  request?: ListSkillDistributionsRequest | undefined,
  security?: ListSkillDistributionsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SkillDistributionsInfiniteQueryData,
    SkillDistributionsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    SkillDistributionsInfiniteQueryData,
    SkillDistributionsPageParams
  >,
  SkillDistributionsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SkillDistributionsInfiniteQueryData,
    SkillDistributionsQueryError,
    InfiniteData<
      SkillDistributionsInfiniteQueryData,
      SkillDistributionsPageParams
    >,
    QueryKey,
    SkillDistributionsPageParams
  >({
    ...buildSkillDistributionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSkillDistributionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      skillId?: string | undefined;
      pluginId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillDistributionsQueryData,
): SkillDistributionsQueryData | undefined {
  const key = queryKeySkillDistributions(...queryKeyBase);

  return client.setQueryData<SkillDistributionsQueryData>(key, data);
}

export function invalidateSkillDistributions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      skillId?: string | undefined;
      pluginId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listDistributions", ...queryKeyBase],
  });
}

export function invalidateAllSkillDistributions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listDistributions"],
  });
}
